import { type ReactNode } from "react";
import { Route, Routes } from "react-router-dom";
import { NotFoundScreen } from "../screens/placeholders.tsx";
import { SystemScreen } from "../screens/system/SystemScreen.tsx";
import { AlertsScreen } from "../screens/alerts/AlertsScreen.tsx";
import { SourcesScreen } from "../screens/sources/SourcesScreen.tsx";
import { EventsScreen } from "../screens/events/EventsScreen.tsx";
import { EventDetailScreen } from "../screens/events/EventDetailScreen.tsx";
import { OverviewScreen } from "../screens/overview/OverviewScreen.tsx";
import { IntentsScreen } from "../screens/agentdna/IntentsScreen.tsx";
import { IntentDetailPage } from "../screens/agentdna/IntentDetailPage.tsx";
import { IntentFlowPage } from "../screens/agentdna/IntentFlowPage.tsx";
import { AgentsAppsScreen } from "../screens/agentdna/AgentsAppsScreen.tsx";
import { RequestsScreen } from "../screens/agentdna/RequestsScreen.tsx";
import { InteractionsScreen } from "../screens/agentdna/InteractionsScreen.tsx";
import { ObservabilityScreen } from "../screens/agentdna/ObservabilityScreen.tsx";
import { ServiceScreen } from "../screens/service/ServiceScreen.tsx";
import { DecisionsScreen } from "../screens/decisions/DecisionsScreen.tsx";
import { IdentityScreen } from "../screens/identity/IdentityScreen.tsx";
import { McpGatewayScreen } from "../screens/mcp-gateway/McpGatewayScreen.tsx";
import { DiscoveredAgentsScreen } from "../screens/discovered/DiscoveredAgentsScreen.tsx";
import { IdentityMcpScreen } from "../screens/identity-mcp/IdentityMcpScreen.tsx";
import { AssuranceScreen } from "../screens/assurance/AssuranceScreen.tsx";
import { EnforcementScreen } from "../screens/enforcement/EnforcementScreen.tsx";
import { IntegrityScreen } from "../screens/integrity/IntegrityScreen.tsx";
import { PostureScreen } from "../screens/posture/PostureScreen.tsx";
import { ComplianceScreen } from "../screens/compliance/ComplianceScreen.tsx";
import { TracesScreen } from "../screens/traces/TracesScreen.tsx";
import { TraceDetailScreen } from "../screens/traces/TraceDetailScreen.tsx";
import { ProfilePage } from "../screens/account/ProfilePage.tsx";
import { TeamMembersScreen } from "../screens/account/TeamMembersScreen.tsx";
import { TeamsScreen } from "../screens/account/TeamsScreen.tsx";
import { ApprovePage } from "../screens/approve/ApprovePage.tsx";

/**
 * The route table for every screen in design 12.2. Every screen is now its real module (tasks
 * 11.4–11.15 are complete); only the in-shell not-found remains in `placeholders.tsx`. Unknown
 * paths fall to the not-found so the chrome never disappears — the SPA fallback in `wrangler.jsonc`
 * serves `index.html` for any deep link and client routing takes it from there.
 *
 * `NAV_ITEMS` is the single list the top-bar navigation and the command palette both read, so a new
 * screen is one entry. The dynamic `/services/:service` route is reachable from the overview and the
 * command palette's "Go to Services" lands on the list form once that screen exists; for now the
 * nav links to the overview and the service route is matched when navigated to directly.
 */

export interface NavItem {
  readonly path: string;
  readonly label: string;
  /** Key into the sidebar icon set (see Chrome.tsx `NAV_ICONS`). */
  readonly icon: string;
}

/**
 * A sidebar group: a labelled, collapsible section holding related destinations. The sidebar renders
 * these as a single-open accordion (the group containing the current page is expanded). `NAV_ITEMS`
 * stays the flat source of truth for the command palette and breadcrumb; `NAV_GROUPS` is only how the
 * sidebar organizes the same destinations.
 */
export interface NavGroup {
  /** Stable id for the open/closed state and the accordion region. */
  readonly id: string;
  /** The group header label. */
  readonly label: string;
  /** A representative icon for the collapsed header and the icon-rail. */
  readonly icon: string;
  /** The destinations in this group, in order. */
  readonly items: readonly NavItem[];
}

/** Top-level destinations shown in the chrome nav and offered by the command palette. */
export const NAV_ITEMS: readonly NavItem[] = [
  { path: "/", label: "Overview", icon: "overview" },
  { path: "/intents", label: "Intents", icon: "intents" },
  { path: "/agents-apps", label: "Agents & Apps", icon: "agents" },
  { path: "/discovered-agents", label: "Discovered agents", icon: "discovered-agents" },
  { path: "/requests", label: "Requests", icon: "requests" },
  { path: "/interactions", label: "Interactions", icon: "interactions" },
  { path: "/observability", label: "Observability", icon: "observability" },
  { path: "/decisions", label: "Decisions", icon: "decisions" },
  { path: "/identity", label: "Identity", icon: "identity" },
  { path: "/mcp-gateway", label: "MCP Gateway", icon: "mcp-gateway" },
  { path: "/identity-mcp", label: "Identity MCP", icon: "identity-mcp" },
  { path: "/assurance", label: "Code Assurance", icon: "assurance" },
  { path: "/enforcement", label: "Enforcement", icon: "enforcement" },
  { path: "/integrity", label: "Integrity", icon: "integrity" },
  { path: "/posture", label: "Posture", icon: "posture" },
  { path: "/compliance", label: "Compliance", icon: "compliance" },
  { path: "/traces", label: "Traces", icon: "traces" },
  { path: "/events", label: "Events", icon: "events" },
  { path: "/alerts", label: "Alerts", icon: "alerts" },
  { path: "/sources", label: "Sources", icon: "sources" },
  { path: "/system", label: "System", icon: "system" },
];

/** A NAV_ITEMS lookup by path, so the groups below reference the single source of truth. */
const BY_PATH: ReadonlyMap<string, NavItem> = new Map(NAV_ITEMS.map((n) => [n.path, n]));
const pick = (path: string): NavItem => {
  const item = BY_PATH.get(path);
  if (item === undefined) throw new Error(`unknown nav path ${path}`);
  return item;
};

/**
 * The sidebar's grouped, accordion layout. Overview stays an ungrouped link at the very top (handled
 * in the sidebar). The groups cluster destinations by function: the agent-governance surface, the
 * combined analytics + investigators surface (decisions/identity/enforcement/posture plus
 * traces/events/alerts), and the operational screens.
 */
export const NAV_GROUPS: readonly NavGroup[] = [
  {
    id: "agents",
    label: "Agents",
    icon: "agents",
    items: [
      pick("/intents"),
      pick("/agents-apps"),
      pick("/discovered-agents"),
      pick("/requests"),
      pick("/interactions"),
      pick("/observability"),
    ],
  },
  {
    // Analytics merges the former "Analytics" and "Investigate" groups and pulls in Alerts: the
    // decision/identity/enforcement/posture analytics alongside the traces/events/alerts
    // investigators, so related destinations live together.
    id: "analytics",
    label: "Analytics",
    icon: "decisions",
    items: [
      pick("/decisions"),
      pick("/identity"),
      pick("/mcp-gateway"),
      pick("/identity-mcp"),
      pick("/assurance"),
      pick("/enforcement"),
      pick("/posture"),
      pick("/compliance"),
      pick("/traces"),
      pick("/events"),
      pick("/alerts"),
    ],
  },
  {
    id: "operations",
    label: "Operations",
    icon: "system",
    items: [pick("/sources"), pick("/system"), pick("/integrity")],
  },
];

/** The ungrouped top-level link shown above the groups. */
export const NAV_OVERVIEW: NavItem = pick("/");

export function AppRoutes(): ReactNode {
  return (
    <Routes>
      <Route path="/" element={<OverviewScreen />} />
      <Route path="/intents" element={<IntentsScreen />} />
      <Route path="/intents/:intentId" element={<IntentDetailPage />} />
      <Route path="/intents/:intentId/flow" element={<IntentFlowPage />} />
      <Route path="/agents-apps" element={<AgentsAppsScreen />} />
      <Route path="/discovered-agents" element={<DiscoveredAgentsScreen />} />
      <Route path="/requests" element={<RequestsScreen />} />
      <Route path="/interactions" element={<InteractionsScreen />} />
      <Route path="/observability" element={<ObservabilityScreen />} />
      <Route path="/services/:service" element={<ServiceScreen />} />
      <Route path="/decisions" element={<DecisionsScreen />} />
      <Route path="/identity" element={<IdentityScreen />} />
      <Route path="/mcp-gateway" element={<McpGatewayScreen />} />
      <Route path="/identity-mcp" element={<IdentityMcpScreen />} />
      <Route path="/assurance" element={<AssuranceScreen />} />
      <Route path="/enforcement" element={<EnforcementScreen />} />
      <Route path="/integrity" element={<IntegrityScreen />} />
      <Route path="/posture" element={<PostureScreen />} />
      <Route path="/compliance" element={<ComplianceScreen />} />
      <Route path="/traces" element={<TracesScreen />} />
      <Route path="/traces/:id" element={<TraceDetailScreen />} />
      <Route path="/events" element={<EventsScreen />} />
      <Route path="/events/:id" element={<EventDetailScreen />} />
      <Route path="/alerts" element={<AlertsScreen />} />
      <Route path="/sources" element={<SourcesScreen />} />
      <Route path="/system" element={<SystemScreen />} />
      {/* Account + admin screens (controlplane-auth task 13), under the protected Shell. The
          account screen is for every operator; the team-members screen is admin-only — the screen
          itself hides its management surface from non-admins (the server still enforces). */}
      <Route path="/account" element={<ProfilePage />} />
      <Route path="/members" element={<TeamMembersScreen />} />
      {/* Superadmin-only; the screen itself refuses a non-superadmin (the server enforces too). */}
      <Route path="/teams" element={<TeamsScreen />} />
      {/* C2: the license holder approves an agent enrollment (the AIS /activate page redirects here
          with ?user_code=). Any signed-in role; the BFF and the AIS enforce step-up and approver. */}
      <Route path="/approve" element={<ApprovePage />} />
      <Route path="*" element={<NotFoundScreen />} />
    </Routes>
  );
}
